import { createContext, useState, useEffect, useContext, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { api } from "../services/api";
import { logger } from "../services/logger";

const AuthContext = createContext();

const ALL_COMPANIES_TOKEN = "ALL";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  useEffect(() => {
    logger.info("AuthProvider: Configurando listener onAuthStateChange...");
    setLoading(true);

    const { data: subscriptionData } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.info(`AuthProvider: Evento onAuthStateChange recebido: ${event}`);

        if (session?.user) {
          setUser(session.user);
          logger.info("AuthProvider: Usuário da sessão Supabase definido.");

          try {
            logger.info("AuthProvider: Buscando perfil do usuário no backend...");
            const profileData = await api.get("/me", {
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setProfile(profileData);
            logger.info("AuthProvider: Perfil do backend definido.", profileData);

            if (profileData?.is_superuser) {
              try {
                const companies = await api.get("/admin/companies", {
                  headers: { Authorization: `Bearer ${session.access_token}` },
                });
                setAvailableCompanies(companies ?? []);
                setSelectedCompanyId((prev) => prev ?? ALL_COMPANIES_TOKEN);
              } catch (companyError) {
                logger.error("AuthProvider: Erro ao listar empresas:", companyError);
                setAvailableCompanies([]);
                setSelectedCompanyId(ALL_COMPANIES_TOKEN);
              }
            } else {
              setAvailableCompanies([]);
              setSelectedCompanyId(profileData?.company_id || null);
            }
          } catch (error) {
            logger.error("AuthProvider: Erro ao buscar perfil do backend.", error);
            setProfile(null);
            setAvailableCompanies([]);
            setSelectedCompanyId(null);
          }
        } else {
          logger.info("AuthProvider: Nenhuma sessão encontrada. Limpando usuário e perfil.");
          setUser(null);
          setProfile(null);
          setAvailableCompanies([]);
          setSelectedCompanyId(null);
        }
        setLoading(false);
      }
    );

    return () => {
      logger.info("AuthProvider: Cancelando inscrição do listener...");
      subscriptionData?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!profile) {
      return;
    }

    if (!profile.is_superuser) {
      setSelectedCompanyId(profile.company_id || null);
    } else {
      setSelectedCompanyId((prev) => prev ?? ALL_COMPANIES_TOKEN);
    }
  }, [profile]);

  const companyScopeQuery = useMemo(() => {
    if (!profile?.is_superuser) {
      return "";
    }

    if (selectedCompanyId === ALL_COMPANIES_TOKEN) {
      return "allCompanies=true";
    }

    return selectedCompanyId ? `companyId=${selectedCompanyId}` : "";
  }, [profile?.is_superuser, selectedCompanyId]);

  const effectiveCompanyId = useMemo(() => {
    if (profile?.is_superuser) {
      return selectedCompanyId === ALL_COMPANIES_TOKEN ? null : selectedCompanyId;
    }
    return profile?.company_id || null;
  }, [profile, selectedCompanyId]);

  const value = useMemo(
    () => ({
      signUp: (data) => supabase.auth.signUp(data),
      signIn: (data) => supabase.auth.signInWithPassword(data),
      signInWithGoogle: () => supabase.auth.signInWithOAuth({ provider: "google" }),
      signOut: () => supabase.auth.signOut(),
      user,
      profile,
      setProfile,
      loading,
      availableCompanies,
      selectedCompanyId: selectedCompanyId ?? effectiveCompanyId,
      setSelectedCompanyId,
      companyScopeQuery,
      effectiveCompanyId,
    }),
    [
      user,
      profile,
      loading,
      availableCompanies,
      selectedCompanyId,
      effectiveCompanyId,
      companyScopeQuery,
    ]
  );

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
